import { Directive, Input, Optional, SkipSelf } from '@angular/core';
import { GridAlign, GridColumn, GridFormat } from './data-grid.model';

/**
 * A column declared in the TEMPLATE, replacing a standalone `<dxi-column>`.
 *
 * WHY THIS EXISTS
 * ---------------
 * Roughly 120 of the remaining files declare at least one column in the markup
 * rather than in `columnDefs` - action columns, button columns, a date column
 * pulled out for its `dataType`. The first conversions moved each one into the
 * component's column array by hand, which works but is the single least
 * automatable step in the whole migration, and the one that already cost a lost
 * View/Playback button when it was missed.
 *
 * Letting the column stay where it was written turns those files back into a
 * mechanical template swap:
 *
 *   <dxi-column caption="View" cellTemplate="viewTemplate" [width]="100">
 *   <app-grid-column caption="View" cellTemplate="viewTemplate" [width]="100">
 *
 * Projected columns are appended AFTER the array-driven ones, which is where
 * DevExtreme put them too (markup order, after the *ngFor).
 */
@Directive({ selector: 'app-grid-column', standalone: false })
export class GridColumnDirective {
  /**
   * The enclosing column, when this one is nested - a BANDED column group:
   *
   *   <app-grid-column caption="Trips">
   *     <app-grid-column dataField="planned"></app-grid-column>
   *     <app-grid-column dataField="actual"></app-grid-column>
   *   </app-grid-column>
   *
   * Resolved through the element injector with @SkipSelf rather than a
   * @ContentChildren({descendants:false}) query on the grid, because columns are
   * routinely wrapped in <ng-container *ngIf> - a non-descendant query would
   * simply not see those, and they would vanish from the grid.
   */
  parent?: GridColumnDirective;

  constructor(@Optional() @SkipSelf() parent?: GridColumnDirective) {
    // NORMALISED TO undefined ON PURPOSE. Angular injects NULL, not undefined,
    // when an @Optional dependency is not found (core's
    // lookupTokenUsingModuleInjector sets notFoundValue = null before falling
    // back to the NullInjector), so a TOP-LEVEL column arrived here with
    // parent === null.
    //
    // The grid builds its header tree by matching the root level with
    // `d.parent === undefined`, which null never satisfies - so every top-level
    // <app-grid-column> was filtered straight back out. Grids that also pass
    // [columns]/[legacyColumns] still showed those, just silently minus their
    // template-declared extras (the Actions/View columns); the 22 grids whose
    // columns come ONLY from the template - profile's My Tickets and the Help
    // Desk Admin list among them - rendered with no header and no cells at all,
    // while the pager still counted the rows underneath.
    this.parent = parent || undefined;
  }

  @Input() dataField?: string;
  @Input() caption?: string;
  @Input() alignment?: GridAlign;
  @Input() width?: number | string;
  @Input() minWidth?: number;
  @Input() visible?: boolean;
  @Input() showInColumnChooser?: boolean;
  @Input() allowSorting?: boolean;
  @Input() allowFiltering?: boolean;
  @Input() allowResizing?: boolean;
  @Input() allowHiding?: boolean;
  @Input() allowGrouping?: boolean;
  @Input() allowEditing?: boolean;
  @Input() allowExporting?: boolean;
  @Input() fixed?: boolean;
  @Input() fixedPosition?: 'left' | 'right';
  @Input() dataType?: 'string' | 'number' | 'date' | 'boolean';
  @Input() groupIndex?: number;
  @Input() sortOrder?: 'asc' | 'desc';
  @Input() sortIndex?: number;
  @Input() cellTemplate?: string;
  @Input() headerCellTemplate?: string;
  @Input() cssClass?: string;
  @Input() calculateCellValue?: (row: any) => any;
  @Input() format?: GridFormat;
  /**
   * DevExtreme distinguished the filter ROW (allowFiltering) from the header
   * filter DROPDOWN (allowHeaderFiltering); `custom-grid` sets them separately
   * off its column config.
   */
  @Input() allowHeaderFiltering?: boolean;
  /** Whether the search panel looks in this column. */
  @Input() allowSearch?: boolean;
  /** Passthrough payload for the cell template; see GridColumn.meta. */
  @Input() meta?: any;
  @Input() lookup?: GridColumn['lookup'];

  /**
   * `type="buttons"` on a DevExtreme column meant "action column": no data
   * field, not sortable, not filterable, not in the chooser. Accepted so those
   * declarations port verbatim.
   */
  @Input() type?: string;

  toGridColumn(): GridColumn {
    const isButtons = this.type === 'buttons';
    return {
      dataField: this.dataField,
      caption: this.caption,
      alignment: this.alignment || 'left',
      width: this.width,
      minWidth: this.minWidth,
      visible: this.visible !== false,
      showInColumnChooser: isButtons ? false : this.showInColumnChooser,
      allowSorting: isButtons ? false : this.allowSorting,
      allowFiltering: isButtons ? false : this.allowFiltering,
      allowResizing: this.allowResizing,
      allowHiding: this.allowHiding,
      allowGrouping: isButtons ? false : this.allowGrouping,
      allowEditing: isButtons ? false : this.allowEditing,
      allowExporting: this.allowExporting,
      fixed: this.fixed,
      fixedPosition: this.fixedPosition,
      dataType: this.dataType,
      groupIndex: this.groupIndex,
      sortOrder: this.sortOrder,
      sortIndex: this.sortIndex,
      cellTemplate: this.cellTemplate,
      headerCellTemplate: this.headerCellTemplate,
      cssClass: this.cssClass,
      calculateCellValue: this.calculateCellValue,
      format: this.format,
      allowHeaderFiltering: this.allowHeaderFiltering,
      allowSearch: this.allowSearch,
      meta: this.meta,
      lookup: this.lookup,
    };
  }
}
